// Task 상세 (v5 §12 시안 4c) — 좌: 지금 내 판단(열린 제안) · 공동 이해 · 질문/넘김 · 참여자,
// 우: 계보 · 출처. 기능은 개편 전과 같다 — 바뀐 것은 배치와 물성뿐이다.
//
// 시안에서 옮기지 못한 것: 「되돌려 보내기」 버튼 — 그런 operation 이 어휘에 없다.
// 반대(rationale 필수)와 수락만 존재한다.
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { useLocation } from 'wouter-preact';
import { command, read, registerResource } from '../api/client.js';
import { taskResourceKey, useCommand, useResource } from '../api/resource.js';
import { describeError } from '../api/errors.js';
import { useSession } from '../session/session.jsx';
import { withParticle } from '../ui/korean-particle.js';
import { Button, ButtonLink, Chip, Dialog, EmberGlyph, Field, Form, Input, Notice, Panel, Select, Textarea } from '../ui/primitives.jsx';
import { Resource } from '../ui/states.jsx';
import { isActiveTaskParticipant, isEligibleTaskRecipient } from './task-members.js';
import { assetSourceSummary, fileSizeLabel, resourceLink, sourceTypeOf } from './resource-directory.js';
import { readProposal } from './proposal-presentation.js';
import { ProposalDecision } from './ProposalDecision.jsx';
import {
  manifestVersionLabel, nativeThreadLink, originRevisionLabel, pinnedAssetVersion,
  proposalChoices, proposalLabel, readResult, readWorkPackage, readinessLabel, readinessTone,
  resultReferenceChoices, sourceLabel, targetLabel, textLines,
} from './handoff-presentation.js';
import { clearDraft, clearScope, draftScope, isAccessDenied, readDraft, writeDraft } from './task-drafts.js';
import { ReferencePicker } from './ReferencePicker.jsx';
import { ResourceUploader, UploadUnavailableNotice, useUploadLimits } from './ResourceUploader.jsx';
import { moveSelectedId, referenceIdentity, toggleSelectedIds } from './reference-picker.js';
import './task-detail.css';

export function TaskDetailPane({ spaceId, taskId, archived, home }) {
  const session = useSession();
  const pack = useResource(
    taskResourceKey(spaceId, taskId),
    () => read('task-context-pack', { nodeId: session.nodeId, query: { spaceId, taskId } }),
  );
  // 상세도 목록과 똑같이 Ledger가 계산해 Product 표면으로 준 liveness만 쓴다.
  const liveness = useResource(
    taskResourceKey(spaceId, taskId, 'liveness'),
    () => read('task-liveness', { nodeId: session.nodeId, query: { spaceId, taskIds: taskId } }),
  );
  const threads = useResource(
    `space:${spaceId}:threads`,
    () => read('conversation-list', { nodeId: session.nodeId, query: { spaceId } }),
  );
  const [promotion, setPromotion] = useState(null);

  // useCommand가 성공 뒤 Context Pack을 무효화한다. 새 서버 응답이 도착한 뒤에만 승격
  // 결과를 말한다. 수락 응답만 보고 화면이 승격을 선언하면 동시 판단을 잘못 귀속시킨다.
  useEffect(() => {
    if (!promotion || promotion.ready || pack.status !== 'ready' || pack.data === promotion.beforePack) return;
    setPromotion((prior) => ({ ...prior, ready: true }));
  }, [pack.data, pack.status, promotion]);

  // ── 아직 안 보낸 것이 사는 자리 ──────────────────────────────────────────
  //
  // **이 자리가 `<Resource>` 보다 위인 것이 요점이다.** 안에 두면 무효화 한 번에 사라진다:
  // 자료를 연결하면 Task 맥락이 무효화되고, 그 순간 아래 전부가 unmount 된다. 성공한
  // 연결이 사람이 쓰던 글을 지우는 것이 실제로 재현됐다.
  //
  // 열려 있는지(`composer`·`returning`)와 무엇을 적었는지(초안 저장소)를 둘 다 위로 올린다.
  // 하나만 올리면 글은 남고 작성기가 닫힌다.
  const scope = draftScope({ spaceId, taskId, nodeId: session.nodeId });
  const [composer, setComposer] = useState(null);
  const [returning, setReturning] = useState(null);

  // Task 나 말하는 Node 가 바뀌면 그건 다른 자리다. 열린 작성기도, 초안도 따라가지 않는다.
  useEffect(() => {
    setComposer(null);
    setReturning(null);
    return () => clearScope(scope);
  }, [scope]);

  // **권한으로 거절된 읽기는 초안도 끝낸다.** 잠깐 못 읽는 것(네트워크·투영 지연)과
  // 이제 못 보는 것은 다르고, 그 판단은 서버가 준 코드로만 한다.
  useEffect(() => {
    if (pack.status !== 'error' || !isAccessDenied(pack.error)) return;
    clearScope(scope);
    setComposer(null);
    setReturning(null);
  }, [pack.status, pack.error, scope]);

  const workspace = {
    scope,
    composer,
    openComposer: setComposer,
    returning,
    openReturn: setReturning,
  };

  return <section class="work"><Resource resource={pack} loadingLabel="Task를 읽는 중">{(data) => (
    <TaskView
      data={data}
      spaceId={spaceId}
      archived={archived}
      home={home}
      threads={threads}
      liveness={liveness}
      promotion={promotion}
      workspace={workspace}
      onAccepted={(beforeVersion, beforeOpenProposals, proposalId) => setPromotion({ beforePack: data, beforeVersion, beforeOpenProposals, proposalId, ready: false })}
    />
  )}</Resource></section>;
}

/**
 * 초안 하나를 컴포넌트 밖 저장소에 붙든다.
 *
 * `useState` 가 아닌 이유가 전부다: 이 컴포넌트는 Task 맥락이 무효화될 때마다 unmount 된다.
 * 저장소는 그대로 있고, 다시 mount 되면 적던 글이 그 자리에 있다.
 */
function useDraft(scope, kind, initial) {
  const [value, setValue] = useState(() => readDraft(scope, kind) ?? initial);
  // 저장소가 정본이고 컴포넌트 상태는 그 그림자다. 상태 갱신 함수 안에서 저장하지 않는다 —
  // 갱신 함수는 순수해야 하고, 여기서 쓰면 한 번의 입력이 두 번 저장될 수 있다.
  const current = useRef(value);
  current.current = value;
  const update = useCallback((patch) => {
    const next = typeof patch === 'function' ? patch(current.current) : { ...current.current, ...patch };
    current.current = next;
    writeDraft(scope, kind, next);
    setValue(next);
  }, [scope, kind]);
  const discard = useCallback(() => {
    clearDraft(scope, kind);
    current.current = initial;
    setValue(initial);
  }, [scope, kind]);
  return [value, update, discard];
}

function TaskView({ data, spaceId, archived, home, threads, liveness, promotion, workspace, onAccepted }) {
  const session = useSession();
  const task = data.task;
  const members = new Map((home.members ?? []).map((member) => [member.nodeId, member]));
  const participants = (data.participants ?? [])
    .filter(isActiveTaskParticipant)
    .map((participant) => ({ ...participant, displayName: members.get(participant.nodeId)?.displayName }));
  const name = (nodeId) => nodeId === session.nodeId ? '나' : (members.get(nodeId)?.displayName ?? '이름 없는 자리');
  const openQuestions = (data.questions ?? []).filter((question) => question.state === 'open' && question.recipientNodeIds.length > 0);
  const openHandoffs = (data.handoffs ?? []).filter((handoff) => ['offered', 'accepted'].includes(handoff.state));
  const closedQuestions = (data.questions ?? []).filter((question) => ['answered', 'withdrawn'].includes(question.state));
  const closedHandoffs = (data.handoffs ?? []).filter((handoff) => ['returned', 'declined'].includes(handoff.state));
  const proposals = data.openProposals ?? [];
  const supersededProposals = data.supersededProposals ?? [];
  const writable = !archived && ['owner', 'participant'].includes(task.viewerRole);
  const canManageParticipants = !archived && task.viewerRole === 'owner';
  // 작성기의 열림 상태는 **위에서** 온다(TaskDetailPane). 여기 두면 무효화 한 번에 닫힌다.
  const { composer, openComposer: setComposer } = workspace;
  const [expanded, setExpanded] = useState(null);

  // Space 의 자료 manifest. Task 는 자료의 이름·주소를 복제하지 않으므로(Ledger 는 locator
  // 만 갖는다), 사람이 알아볼 이름과 원문 위치는 여기서 다시 읽는다. 넘김 꾸러미도 같은
  // 목록을 쓴다 — 꾸러미가 자기 사본을 들고 다니게 두지 않는다.
  const assets = useResource(`space:${spaceId}:resources`, () =>
    read('resources', { nodeId: session.nodeId, query: { spaceId } }),
  );

  // 「원문 대화 열기」 — 읽을 수 있는 첫 thread 참조. 없으면 버튼을 그리지 않는다.
  const originRef = (data.references ?? []).find((reference) =>
    reference.locator?.threadId && reference.providerAvailable && reference.capabilities?.read && reference.transport === 'native');

  return <>
    <div class="work__crumb t-meta"><span>Task</span><span>/</span><span style="color: var(--ink-2)">{task.title}</span></div>
    <header class="work__head">
      <div class="work__head-text">
        <h1 class="t-view">{task.title}</h1>
        <div class="row" style="margin-top: var(--space-2)">
          <Chip tone="accepted">{task.currentVersionNo ? `공동 이해 v${task.currentVersionNo}` : '공동 이해 v0'}</Chip>
          <span class="t-meta">오너 {name(task.ownerNodeId)} · 내 역할 {roleLabel(task.viewerRole)}</span>
        </div>
      </div>
      {originRef ? (
        <ButtonLink size="md" href={`/s/${spaceId}/t/${originRef.locator.threadId}`}>원문 대화 열기</ButtonLink>
      ) : null}
    </header>
    <div class="work__body task-detail">
      <PromotionNotice promotion={promotion} task={task} currentVersion={data.currentVersion} openProposalCount={proposals.length} />
      <div class="detail-grid">
        <div class="stack">
          <TaskOrigin task={task} references={data.references ?? []} spaceId={spaceId} name={name} />
          <ProposalsPanel
            proposals={proposals}
            task={task}
            spaceId={spaceId}
            mine={session.nodeId}
            name={name}
            expanded={expanded}
            setExpanded={setExpanded}
            archived={archived}
            onAccepted={onAccepted}
          />
          <Understanding version={data.currentVersion} proposals={proposals} name={name} />
          <PromisesPanel
            liveness={liveness}
            openQuestions={openQuestions}
            openHandoffs={openHandoffs}
            supersededProposals={supersededProposals}
            current={data.currentVersion}
            closedQuestions={closedQuestions}
            closedHandoffs={closedHandoffs}
            task={task}
            spaceId={spaceId}
            mine={session.nodeId}
            name={name}
            writable={writable}
            composer={composer}
            setComposer={setComposer}
            workspace={workspace}
            participants={participants}
            references={data.references ?? []}
            assets={assets}
            pack={data}
          />
          <Participants
            allParticipants={data.participants ?? []}
            homeMembers={home.members ?? []}
            canManageParticipants={canManageParticipants}
            spaceId={spaceId}
            taskId={task.taskId}
            name={name}
          />
        </div>
        <div class="stack">
          <LineagePanel lineage={data.versionLineage} currentVersionNo={task.currentVersionNo} name={name} />
          <References references={data.references ?? []} current={data.currentVersion} threads={threads} spaceId={spaceId} task={task} writable={writable} />
        </div>
      </div>
  </div></>;
}

/**
 * Task가 왜 생겼는지를 설명하는 곳. Ledger에는 locator만 있고 원문 복사본은 없으므로,
 * 대화 제공자에서 지금 다시 읽은 순서 있는 원문만 여기에 그린다. 이렇게 해야
 * Task의 현재 이해와 원문 기록이 서로를 덮어쓰지 않는다.
 */
function TaskOrigin({ task, references, spaceId, name }) {
  const origin = references.find((reference) =>
    reference.relation === 'origin'
    && reference.kind === 'thread'
    && reference.mode === 'snapshot'
    && reference.locator?.threadId
    && Number.isInteger(reference.locator?.throughSequence));

  if (!origin) {
    return <Panel title="Task가 시작된 이유">
      <p class="t-body">{task.seedPurpose ?? '시작 목적을 읽을 수 없습니다.'}</p>
      <p class="t-meta">원문 범위 locator가 없어, 시작 기록을 다시 열 수 없습니다.</p>
    </Panel>;
  }

  return <Panel
    title="Task가 시작된 원문"
    action={<ButtonLink size="sm" href={`/s/${spaceId}/t/${origin.locator.threadId}`}>원문 대화 열기</ButtonLink>}
  >
    <div class="stack">
      <div class="task-origin__purpose">
        <p class="t-meta">사람이 정한 작업 목적</p>
        <p class="t-body">{task.seedPurpose ?? '시작 목적을 읽을 수 없습니다.'}</p>
      </div>
      <p class="t-meta">원문 #1–#{origin.locator.throughSequence}을 이 Task의 시작점으로 고정해 참조합니다.</p>
      <TaskOriginMessages
        spaceId={spaceId}
        threadId={origin.locator.threadId}
        throughSequence={origin.locator.throughSequence}
        name={name}
      />
    </div>
  </Panel>;
}

function TaskOriginMessages({ spaceId, threadId, throughSequence, name }) {
  const session = useSession();
  const conversation = useResource(
    `task-origin:${spaceId}:${threadId}:${throughSequence}`,
    () => read('conversation-read', { nodeId: session.nodeId, query: { spaceId, threadId } }),
  );

  return <Resource resource={conversation} loadingLabel="Task의 시작 원문을 다시 읽는 중">
    {(data) => {
      const messages = (data.messages ?? []).filter((message) => message.sequence <= throughSequence);
      if (!messages.length) return <p class="t-meta">이 범위의 원문을 지금 읽을 수 없습니다.</p>;
      return <ol class="task-origin__messages" aria-label="Task 시작 원문">
        {messages.map((message) => (
          <li key={message.messageId} class="task-origin__message">
            <p class="t-meta">#{message.sequence} · {name(message.authorNodeId)}</p>
            <p class="t-body">{message.retracted ? '작성자가 철회한 기록입니다.' : message.body}</p>
          </li>
        ))}
      </ol>;
    }}
  </Resource>;
}

/** 열린 제안 — 사람 직접 판단 gate 가 사는 자리(시안 4c 상단). */
function ProposalsPanel({ proposals, task, spaceId, mine, name, expanded, setExpanded, archived, onAccepted }) {
  if (proposals.length === 0) {
    return <Panel title="열린 제안">
      <p class="t-meta">지금 열린 제안이 없습니다. 다음 제안이 올라오면 이 자리가 판단의 입구가 됩니다.</p>
    </Panel>;
  }
  return <Panel class="panel panel--gate" title="열린 제안 — 지금 내 판단" action={<span class="t-meta t-mono">{proposals.length}건</span>}>
    <div class="promise-list">
      {proposals.map((proposal) => (
        <ProposalRow
          key={proposal.proposalId}
          proposal={proposal}
          task={task}
          spaceId={spaceId}
          mine={mine}
          name={name}
          expanded={expanded === proposal.proposalId || proposals.length === 1}
          setExpanded={setExpanded}
          archived={archived}
          openProposalCount={proposals.length}
          onAccepted={onAccepted}
        />
      ))}
    </div>
  </Panel>;
}

function Understanding({ version, proposals, name }) {
  // 승격을 만든 제안의 판단 기록. Ledger 가 Context Pack 에 실어 준 것만 읽는다(E-32) —
  // 옛 배포의 서버는 이 칸을 주지 않으므로 그때는 문장만 남는다. 지어내지 않는다.
  const promoted = version?.promotedFromProposal ?? null;
  return <Panel title={version ? `현재 공동 이해 v${version.versionNo}` : '현재 공동 이해'}
    action={version ? <Chip tone="accepted">사람 직접 수락</Chip> : null}>
    {version ? <div class="stack">
      <p class="t-meta">{promoted ? `제안 p${promoted.number}에서 승격됨` : '제안에서 승격됨'}</p>
      <p class="t-body">{version.purpose}</p>
      <Facts label="확정한 사실" items={version.confirmed} empty="아직 확정한 사실이 없습니다." />
      <Facts label="아직 모르는 것" items={version.openQuestions} empty="없습니다." />
      <p class="t-meta">근거 {version.referenceIds.length}건</p>
      {promoted ? <div>
        <p class="t-meta">이 이해를 만든 판단 기록 — 반대도 공동 기억입니다.</p>
        {promoted.responses.length
          ? promoted.responses.map((response) => <p class="t-meta" key={response.responseId}>{name(response.actorNodeId)} · {response.response === 'accept' ? '수락' : '반대'} · {provenance(response.actorProvenance)}{response.rationale ? ` · ${response.rationale}` : ''}</p>)
          : <p class="t-meta">응답 기록이 없습니다.</p>}
      </div> : null}
    </div> : <div class="stack">
      <p class="t-body">아직 함께 확정한 이해가 없습니다 (v0).</p>
      {proposals.some((proposal) => proposal.number === 1) ? <p class="t-meta">첫 제안 p1이 위에 열려 있습니다. 아직 공동 이해가 아닙니다.</p> : null}
    </div>}
  </Panel>;
}

/** 공동 이해 계보 — Ledger 가 실어 준 versionLineage 만 읽는다. 없으면 없다고 적는다. */
function LineagePanel({ lineage, currentVersionNo, name }) {
  if (!currentVersionNo) {
    return <Panel title="공동 이해 계보"><p class="t-meta">아직 승격된 이해가 없습니다.</p></Panel>;
  }
  if (!Array.isArray(lineage) || lineage.length === 0) {
    return <Panel title="공동 이해의 기록"><p class="t-meta">아직 이전 이해 기록을 읽을 수 없습니다.</p></Panel>;
  }
  return <Panel title="공동 이해 계보">
    <div class="lineage">
      {lineage.map((entry, index) => (
        <div class="lineage__item" key={entry.versionNo}>
          <div class="lineage__spine">
            <span class="lineage__dot" aria-hidden="true" />
            {index < lineage.length - 1 ? <span class="lineage__line" aria-hidden="true" /> : null}
          </div>
          <div class="lineage__body">
            <p class="t-body-strong">v{entry.versionNo} · {entry.purpose}</p>
            <p class="t-meta">
              {entry.promotedByNodeId ? `${name(entry.promotedByNodeId)} 직접 수락` : '수락 기록을 읽지 못했습니다'}
              {entry.createdAt ? ` · ${date(entry.createdAt)}` : ''}
            </p>
          </div>
        </div>
      ))}
    </div>
  </Panel>;
}

function PromotionNotice({ promotion, task, currentVersion, openProposalCount }) {
  if (!promotion?.ready) return null;
  const promoted = currentVersion?.promotedFromProposalId === promotion.proposalId;
  const displaced = Math.max(0, promotion.beforeOpenProposals - 1 - openProposalCount);
  return <Notice tone="neutral" title={promoted
    ? `runbook 종결 정책이 충족되어 ${withParticle(`공동 이해 v${task.currentVersionNo}`, 'subject')} 기록됐습니다.`
    : task.currentVersionNo !== promotion.beforeVersion
      ? `판단을 기록했습니다. 현재 공동 이해는 v${task.currentVersionNo}입니다. 선택한 제안의 승격은 확인되지 않았습니다.`
      : '수락 1건이 기록됐습니다. 아직 승격 정책이 충족되지 않았습니다.'}
  >{promoted && displaced > 0 ? <p>승격으로 다른 열린 제안 {displaced}건이 대체되었습니다.</p> : null}</Notice>;
}

function Participants({ allParticipants, homeMembers, canManageParticipants, spaceId, taskId, name }) {
  const active = allParticipants.filter(isActiveTaskParticipant);
  const departed = allParticipants.filter((participant) => participant.removedAt !== null);
  const activeIds = new Set(active.map((participant) => participant.nodeId));
  // 후보 판정은 Task Composer와 같은 isEligibleTaskRecipient 하나만 쓴다.
  const candidates = homeMembers.filter((member) => isEligibleTaskRecipient(member) && !activeIds.has(member.nodeId));
  const add = taskCommand('task_add_participant', spaceId);
  const remove = taskCommand('task_remove_participant', spaceId);
  return <Panel title={`참여자 ${active.length}명`}><div class="stack"><div class="promise-list">{active.map((participant) => {
    const member = homeMembers.find((m) => m.nodeId === participant.nodeId);
    const ai = member?.kind === 'ai';
    return <article class="participant-row" key={participant.nodeId}><div class="participant-row__who"><ParticipantAvatar label={name(participant.nodeId)} ai={ai} /><div><p class="t-body-strong">{name(participant.nodeId)}</p><p class="t-meta">{participant.role === 'owner' ? '오너' : participant.role === 'participant' ? '참여자' : '관찰자'}{ai ? ' · AI' : ''}</p></div></div>{canManageParticipants && participant.role !== 'owner' ? <Button size="sm" busy={remove.pending} onClick={() => remove.run({ spaceId, taskId, nodeId: participant.nodeId })}>이 Task에서 내보냄</Button> : null}</article>;
  })}</div>
    {departed.length ? <div class="stack"><p class="t-meta">이전 참여</p>{departed.map((participant) => <p class="t-meta" key={participant.nodeId}>{name(participant.nodeId)} · 이 Task에서 내보냄 · 남긴 판단과 발화는 기록으로 남습니다.</p>)}</div> : null}
    {canManageParticipants ? (candidates.length ? <Form class="task-form stack" busy={add.pending} onSubmit={(event) => {
      const nodeId = new FormData(event.currentTarget).get('nodeId');
      if (nodeId) add.run({ spaceId, taskId, nodeId, role: 'participant' });
    }}><Field label="참여자 추가" htmlFor={`participant-${taskId}`} required><Select id={`participant-${taskId}`} name="nodeId"><option value="">고르기</option>{candidates.map((member) => <option value={member.nodeId} key={member.nodeId}>{member.displayName ?? '이름 없는 자리'}</option>)}</Select></Field><CommandError cmd={add}/><Button kind="submit" size="md" busy={add.pending}>참여자 추가</Button></Form> : <p class="t-meta">추가할 활성 Space 구성원이 없습니다.</p>) : null}
    <CommandError cmd={remove}/>
  </div></Panel>;
}

function PromisesPanel({ liveness, openQuestions, openHandoffs, supersededProposals, current, closedQuestions, closedHandoffs, task, spaceId, mine, name, writable, composer, setComposer, participants, references, assets, pack, workspace }) {
  return <Resource resource={liveness} loadingLabel="미결 약속을 읽는 중">{(data) => {
    const summary = data.tasks?.[0];
    if (!summary) return <Notice tone="danger" title="미결 약속을 읽지 못했습니다." />;
    const count = countTotal(summary.counts);
    return <Panel title="열린 질문 · 넘김" action={<EmberGlyph count={count} />}><div class="stack">
      {count === 0 ? <p class="t-meta">휴면 · 지금 기다리는 것 없음</p> : <p class="t-meta">질문 {summary.counts.questions} · 넘김 {summary.counts.handoffs} · 제안 {summary.counts.proposals}</p>}
      {openQuestions.length || openHandoffs.length ? <div class="promise-list">
        {openQuestions.map((question) => <QuestionRow key={question.questionId} question={question} task={task} spaceId={spaceId} mine={mine} name={name} />)}
        {openHandoffs.map((handoff) => <HandoffRow key={handoff.handoffId} handoff={handoff} spaceId={spaceId} mine={mine} name={name} assets={assets} pack={pack} workspace={workspace} writable={writable} />)}
      </div> : null}
      {closedQuestions.length || closedHandoffs.length || supersededProposals.length ? <details class="promise-history"><summary class="t-body-strong">끝난 약속 {closedQuestions.length + closedHandoffs.length + supersededProposals.length}건</summary><div class="stack">{closedQuestions.map((question) => <ClosedQuestionRow question={question} key={question.questionId} name={name} />)}{closedHandoffs.map((handoff) => <ClosedHandoffRow handoff={handoff} key={handoff.handoffId} name={name} assets={assets} spaceId={spaceId} pack={pack} />)}{supersededProposals.map((proposal) => <SupersededProposalRow proposal={proposal} key={proposal.proposalId} name={name} />)}</div></details> : null}
      {writable ? <><div class="task-actions"><Button size="sm" onClick={() => setComposer('question')}>질문 열기</Button><Button size="sm" onClick={() => setComposer('handoff')}>넘김 제안</Button><Button size="sm" onClick={() => setComposer('proposal')}>제안 올리기</Button></div>{composer ? <Composer kind={composer} close={() => setComposer(null)} task={task} current={current} participants={participants} spaceId={spaceId} references={references} assets={assets} workspace={workspace} /> : null}</> : <p class="t-meta">읽을 수 있지만 이 Task의 판단에는 참여하지 않습니다.</p>}
    </div></Panel>;
  }}</Resource>;
}

function Facts({ label, items, empty }) { return <div><p class="t-meta">{label}</p>{items.length ? <ul class="task-facts">{items.map((item) => <li key={item.id}>{item.text}</li>)}</ul> : <p class="t-meta">{empty}</p>}</div>; }

function QuestionRow({ question, task, spaceId, mine, name }) {
  const [answer, setAnswer] = useState(false);
  const canAnswer = question.recipientNodeIds.includes(mine) && ['owner', 'participant'].includes(task.viewerRole);
  const canWithdraw = question.openedBy === mine;
  const answerCmd = taskCommand('question_answer', spaceId);
  const withdraw = taskCommand('question_withdraw', spaceId);
  return <article class="promise"><div class="row"><Chip tone="waiting">{question.recipientNodeIds.includes(mine) ? '질문 · 나에게' : `질문 · ${question.recipientNodeIds.map(name).join(', ')}에게`}</Chip></div><p class="t-body">“{question.prompt}”</p><p class="t-meta">연 사람 {name(question.openedBy)} · 지목 {question.recipientNodeIds.length}명{question.dueHint ? ` · 기한 힌트 ${date(question.dueHint)}` : ''}</p>{question.dueHint ? <p class="t-meta">힌트일 뿐입니다. 기한이 지나도 서버가 상태를 바꾸지 않습니다.</p> : null}<div class="row">{canAnswer ? <Button size="sm" onClick={() => setAnswer(!answer)}>답하기</Button> : null}{canWithdraw ? <Button size="sm" onClick={() => withdraw.run({ spaceId, questionId: question.questionId })}>철회</Button> : null}</div>{answer ? <Form class="task-form" busy={answerCmd.pending} onSubmit={(event) => { const body = new FormData(event.currentTarget).get('body')?.trim(); if (body) answerCmd.run({ spaceId, questionId: question.questionId, body }); }}><Field label="답변" htmlFor={`answer-${question.questionId}`} required><Textarea id={`answer-${question.questionId}`} name="body" /></Field><CommandError cmd={answerCmd}/><Button kind="submit" size="md" busy={answerCmd.pending}>답변 남기기</Button></Form> : null}</article>;
}

function ClosedQuestionRow({ question, name }) {
  return <details class="promise-history__item"><summary class="t-body-strong">질문 · {question.state === 'answered' ? '답변됨' : '철회됨'} · “{question.prompt}”</summary><div class="stack"><p class="t-meta">연 사람 {name(question.openedBy)}</p>{question.answers?.length ? question.answers.map((answer) => <p class="t-body" key={answer.answerId}>{name(answer.actorNodeId)} · {answer.body}</p>) : <p class="t-meta">{question.state === 'withdrawn' ? '답변 없이 철회되었습니다.' : '답변 기록을 읽지 못했습니다.'}</p>}</div></details>;
}

export function HandoffRow({ handoff, spaceId, mine, name, assets, pack, workspace, writable = true, onResponded }) {
  const accept = taskCommand('handoff_accept', spaceId);
  const decline = taskCommand('handoff_decline', spaceId);
  const uncertain = (cmd) => cmd.error && !(cmd.error.status >= 400 && cmd.error.status < 500 && cmd.error.code);
  const actionLock = useRef(false);
  const respond = async (cmd) => {
    if (actionLock.current) return;
    actionLock.current = true;
    try {
      const result = await cmd.run({ spaceId, handoffId: handoff.handoffId });
      if (result.ok) onResponded?.();
    }
    finally { actionLock.current = false; }
  };
  // 반환 대화상자도 위에서 연다 — 그 안에서 자료를 연결해도 닫히지 않아야 한다.
  const returning = workspace.returning === handoff.handoffId;
  const setReturning = (open) => workspace.openReturn(open ? handoff.handoffId : null);
  const mineTo = handoff.toNodeId === mine && writable;
  return <article class="promise">
    <div class="row"><Chip tone="handoff">{handoff.state === 'offered' ? '넘김 · 수락 대기' : '넘김 · 진행'}</Chip></div>
    <p class="t-body">“{handoff.request}”</p>
    <p class="t-meta">
      {name(handoff.fromNodeId)} → {name(handoff.toNodeId)}
      {handoff.expectedBack ? ` · 돌려받기 희망 ${date(handoff.expectedBack)}` : ''}
      {handoff.authority ? ` · 범위: ${handoff.authority}` : ''}
    </p>
    <WorkPackageView handoff={handoff} spaceId={spaceId} assets={assets} pack={pack} />
    {mineTo ? <div class="row">
      {handoff.state === 'offered'
        ? <>
          <Button size="sm" tone="human" busy={accept.pending} disabled={accept.blocked || decline.pending || uncertain(decline)} onClick={() => respond(accept)}>내가 맡기</Button>
          <Button size="sm" busy={decline.pending} disabled={decline.blocked || accept.pending || uncertain(accept)} onClick={() => respond(decline)}>거절</Button>
        </>
        : <Button size="sm" onClick={() => setReturning(true)}>반환</Button>}
    </div> : <p class="t-meta">{handoff.state === 'offered' ? `${name(handoff.toNodeId)}의 응답을 기다립니다` : `${name(handoff.toNodeId)}가 들고 있습니다`}</p>}
    <CommandError cmd={accept}/>
    <CommandError cmd={decline}/>
    {returning ? <ReturnDialog handoff={handoff} spaceId={spaceId} pack={pack} assets={assets} workspace={workspace} close={() => setReturning(false)} /> : null}
  </article>;
}

/**
 * 넘김이 들고 온 것 — **서버가 준 것만 그린다.**
 *
 * 옛 넘김에는 꾸러미가 없다. 그때는 이 자리가 통째로 없다(빈 꾸러미로 꾸미지 않는다).
 * 보고 상태는 전부 「…라고 보고됨」이다: 그건 받은 쪽 자리의 관측이지 이 Loom 이 허가한
 * 접근이 아니다(handoff-presentation.js).
 */
function WorkPackageView({ handoff, spaceId, assets, pack }) {
  const view = readWorkPackage(handoff);
  if (!view) return null;
  const titles = assetTitleMap(assets);
  // 참조의 provider 능력은 Context Pack 이 참조마다 실어 준다. 꾸러미 항목은 그 값을 갖지
  // 않으므로 여기서 짝지어 준다 — 없으면 화면이 열리지 않는 링크를 그린다.
  const enriched = new Map((pack?.references ?? []).map((reference) => [reference.referenceId, reference]));
  return <section class="handoff-package" aria-label="이 넘김이 들고 가는 것">
    <p class="t-meta">
      {view.baseVersionNo === null ? '기준 공동 이해 없음 (v0)' : `기준 공동 이해 v${view.baseVersionNo}`}
      {' · 제안 시점에 고정됨'}
    </p>
    {view.baseVersionNo === null ? null : <BaseVersionPanel
      spaceId={spaceId}
      taskId={handoff.taskId ?? pack?.task?.taskId}
      versionNo={view.baseVersionNo}
    />}
    {view.expectedResult ? <p class="t-body">돌려받고 싶은 것: {view.expectedResult}</p> : null}
    {view.items.length ? <ul class="handoff-package__list">
      {view.items.map((item) => <li key={item.referenceId} class="handoff-package__item">
        <div class="row">
          <Chip>{item.necessity === 'required' ? '필수' : '참고'}</Chip>
          <Chip tone={readinessTone(item.report?.status)}>
            {item.report ? readinessLabel(item.report.status) : '아직 보고 없음'}
          </Chip>
        </div>
        <p class="t-body">{item.position}. {referenceTitle(item, titles)}</p>
        <p class="t-meta">{targetLabel(item)}</p>
        {item.guidance ? <p class="t-meta">지시: {item.guidance}</p> : null}
        {item.report?.status === 'revision_mismatch' && item.report.observedRevision
          ? <p class="t-meta">약속된 판 {item.report.targetRevision ?? '—'} · 본 판 {item.report.observedRevision}</p>
          : null}
        {item.report?.detail ? <p class="t-meta">{item.report.detail}</p> : null}
        {item.reportCount > 1 ? <p class="t-meta">보고 {item.reportCount}건 중 마지막입니다.</p> : null}
        <ReferenceAction item={{ ...(enriched.get(item.referenceId) ?? {}), ...item }} spaceId={spaceId} assets={assets} />
      </li>)}
    </ul> : <p class="t-meta">고른 근거 없이 기대 결과만 적힌 꾸러미입니다.</p>}
    {view.summary ? <p class="t-meta">
      필수 {view.summary.requiredItems}건 중 {view.summary.requiredReportedReadable}건이 읽힌다고 보고됐습니다.
      보고는 접근 권한이 아니라 받는 쪽 자리의 관측입니다.
    </p> : null}
  </section>;
}

/**
 * 원문으로 가는 길 — **그 참조가 가리키는 판으로.**
 *
 * 자료 참조가 `snapshot` 이면 고정된 판이 있고, 목록에 실린 *현재* 판은 다른 것일 수 있다.
 * 처음 판은 「v1 고정」이라 적어 놓고 링크는 현재 판의 주소를 열었다 — 라벨과 링크가 서로
 * 다른 것을 말하면 둘 다 믿을 수 없어진다. 그래서 고정된 판은 그 판을 직접 다시 읽는다.
 */
export function ReferenceAction({ item, spaceId, assets }) {
  if (item.kind === 'thread' || item.kind === 'message') {
    const threadId = nativeThreadLink(item);
    // 외부 provider 의 대화를 native 경로로 링크하면 누르는 순간 없는 Thread 를 찾는다.
    return threadId
      ? <div class="row"><ButtonLink size="sm" href={`/s/${spaceId}/t/${threadId}`}>대화 열기</ButtonLink></div>
      : <p class="t-meta">이 원문은 이 화면에서 열 수 없습니다. 원본 시스템에서 확인하세요.</p>;
  }
  if (item.kind === 'url' && item.locator?.url) {
    return <div class="row"><a class="t-meta" href={item.locator.url} target="_blank" rel="noreferrer">원문 열기</a></div>;
  }
  if (item.kind !== 'asset') return null;
  const pinned = pinnedAssetVersion(item);
  return pinned === null
    ? <AssetAction manifest={assetById(assets, item.locator?.assetId)} spaceId={spaceId} pinned={null} />
    : <PinnedAssetAction spaceId={spaceId} assetId={item.locator?.assetId} version={pinned} />;
}

/** 고정된 판을 **그 판으로** 다시 읽는다. 목록의 현재 판을 대신 쓰지 않는다. */
function PinnedAssetAction({ spaceId, assetId, version }) {
  const session = useSession();
  const manifest = useResource(
    assetId ? `space:${spaceId}:resource:${assetId}:${version}` : null,
    () => read('resource', { nodeId: session.nodeId, query: { spaceId, assetId, version } }),
  );
  if (manifest.status === 'loading') return <p class="t-meta">고정된 판을 읽는 중입니다.</p>;
  if (manifest.status === 'error') return <p class="t-meta">고정된 판(v{version})을 지금 읽을 수 없습니다.</p>;
  return <AssetAction manifest={manifest.data} spaceId={spaceId} pinned={version} />;
}

function AssetAction({ manifest, spaceId, pinned }) {
  if (!manifest) return <p class="t-meta">자료 위치를 지금 읽을 수 없습니다.</p>;
  const git = sourceLabel(manifest.source);
  const version = manifestVersionLabel(manifest);
  const origin = originRevisionLabel(manifest);
  // 보관 파일에는 밖으로 나가는 주소가 **없다**(`sourceUri: null`). 「원문 열기」를 그리면
  // 그 링크는 아무 데도 가지 않는다. 대신 무엇인지 적고 자료실의 **그 자료**로 보낸다 —
  // 내려받기는 매 요청 권한을 다시 보는 그 화면에만 있다.
  if (sourceTypeOf(manifest) === 'managed_file') {
    const file = manifest.file ?? null;
    return <>
      <p class="t-meta">
        {pinned === null ? '현재 판을 따라갑니다' : version ?? `Loom 등록 판 v${pinned}`}
        {file?.filename ? ` · ${file.filename}` : ''}
        {file?.byteSize ? ` · ${fileSizeLabel(file.byteSize)}` : ''}
        {file?.mediaType ? ` · ${file.mediaType}` : ''}
      </p>
      <div class="row">
        <ButtonLink size="sm" href={resourceLink(spaceId, manifest.assetId, pinned)}>자료 보기</ButtonLink>
      </div>
    </>;
  }
  return <>
    {/* 판을 말하는 두 이름을 나란히 두되 **섞지 않는다**: 앞은 Loom 이 등록한 판,
        뒤는 원본이 자기 식으로 부르는 이름이다. */}
    <p class="t-meta">
      {pinned === null ? '현재 판을 따라갑니다' : version ?? `Loom 등록 판 v${pinned}`}
      {origin ? ` · ${origin}` : ''}
    </p>
    {git ? <p class="t-meta t-mono">{git}</p> : null}
    {manifest.sourceRedacted
      ? <p class="t-meta">이 주소에는 자격으로 보이는 값이 들어 있어 가려서 보여 줍니다. 원문 열기는 제공하지 않습니다.</p>
      : <div class="row">
        <a class="t-meta" href={manifest.sourceUri} target="_blank" rel="noreferrer">원문 열기</a>
        {/* **고정된 참조는 그 판으로 연다.** 자료실이 현재 판을 대신 보여 주면,
            이 넘김이 약속한 것과 받는 사람이 읽는 것이 갈린다. */}
        <ButtonLink size="sm" href={resourceLink(spaceId, manifest.assetId, pinned)}>자료 보기</ButtonLink>
      </div>}
  </>;
}

/** 넘김이 굳혀 둔 기준 이해 — **번호가 아니라 내용으로.** */
function BaseVersionPanel({ spaceId, taskId, versionNo }) {
  const session = useSession();
  const version = useResource(
    `space:${spaceId}:task:${taskId}:version:${versionNo}`,
    () => read('task-version', { nodeId: session.nodeId, query: { spaceId, taskId, versionNo } }),
  );
  if (version.status === 'loading') return <p class="t-meta">기준 이해 v{versionNo}을 읽는 중입니다.</p>;
  if (version.status === 'error') return <p class="t-meta">기준 이해 v{versionNo}을 지금 읽을 수 없습니다.</p>;
  const data = version.data;
  return <details class="handoff-base">
    <summary class="t-meta">기준 공동 이해 v{versionNo} — 무엇을 기준으로 시작하라는 것이었나</summary>
    <div class="stack">
      <p class="t-body">{data.purpose}</p>
      <Facts label="그때 확정한 사실" items={data.confirmed ?? []} empty="없었습니다." />
      <Facts label="그때 아직 모르던 것" items={data.openQuestions ?? []} empty="없었습니다." />
    </div>
  </details>;
}

/**
 * 반환에 결과를 붙이는 자리.
 *
 * 전부 선택이다 — 아무것도 안 적고 메모만 남겨도 예전과 똑같이 반환된다. 다만 **요약 없이
 * 다른 칸만 채우면 그 칸들은 서버에 가지 않는다.** 처음 판은 그걸 조용히 버리고 반환까지
 * 했다. 지금은 보내기 전에 그 자리에서 말하고, 적어 둔 것을 지우지 않는다.
 *
 * **제안 id 를 적어도 그 제안이 승격되지는 않는다.** 승격은 runbook 종결 정책이 충족된
 * 결과이고, 이 화면에는 그걸 부를 수단이 없다(§절대규칙 1).
 */
function ReturnDialog({ handoff, spaceId, pack, assets, workspace, close }) {
  const returned = taskCommand('handoff_return', spaceId);
  const view = readWorkPackage(handoff);
  const titles = assetTitleMap(assets);
  const proposals = proposalChoices(pack);
  // 반환 초안도 컴포넌트 밖에 산다 — 이 안에서 산출 자료를 연결하면 Task 맥락이 무효화되고
  // 대화상자가 통째로 다시 그려진다. 적어 둔 요약·고른 근거가 거기서 사라지면 안 된다.
  const [draft, set, discardDraft] = useDraft(workspace.scope, `return:${handoff.handoffId}`, {
    note: '', summary: '', referenceIds: [], remainingIssues: '', proposalId: '',
    resultQuery: '', resultGroup: 'all',
  });
  const [localError, setLocalError] = useState(null);
  const [connecting, setConnecting] = useState(false);

  // 고를 수 있는 것은 **꾸러미 밖까지**다 — 일하면서 새로 붙인 근거가 보통 결과의 핵심이다.
  const choices = resultReferenceChoices({ references: pack.references ?? [], workPackage: view })
    .map((reference) => ({
      ...referenceChoice(reference, titles, assets, reference.inPackage ? 'package' : 'task'),
      groupLabel: reference.inPackage ? '넘김이 들고 온 것' : null,
    }));

  const structured = draft.referenceIds.length > 0
    || draft.remainingIssues.trim() !== ''
    || draft.proposalId !== '';
  // 초안에 이미 적어 둔 것이 있으면 **접어 두지 않는다.** 접힌 자리에 사라진 입력은
  // 사람이 잃은 입력이다.
  const extraFilled = draft.note.trim() !== '' || draft.remainingIssues.trim() !== '' || draft.proposalId !== '';

  // 제출 버튼과 자료 연결이 폼 **밖**에 있는 이유: `InlineResourceConnect` 는 자기
  // `<form>` 을 갖는다. 폼 안에 폼을 넣으면 브라우저가 안쪽을 버리고, 「여기서 자료 연결」이
  // 조용히 죽는다. 값은 전부 초안 state 에서 오므로 자리와 무관하고, 제출 경로는
  // `form` 속성으로 여전히 **하나**다.
  const formId = `return-form-${handoff.handoffId}`;

  return <Dialog title="넘김 반환" onClose={close}>
    <div class="stack stack--loose">
      <Form id={formId} class="stack" busy={returned.pending} onSubmit={async () => {
        const summary = draft.summary.trim();
        if (!summary && structured) {
          // 적어 둔 것은 그대로 둔다. 화면이 사람의 입력을 버리는 일은 없어야 한다.
          setLocalError('고른 근거·남은 문제·제안을 함께 남기려면 결과 요약이 필요합니다. 메모만 남기려면 아래 칸들을 비워 주세요.');
          return;
        }
        setLocalError(null);
        const outcome = await returned.run({
          spaceId,
          handoffId: handoff.handoffId,
          note: draft.note.trim() || undefined,
          result: summary ? {
            summary,
            referenceIds: draft.referenceIds,
            remainingIssues: textLines(draft.remainingIssues),
            ...(draft.proposalId ? { proposalId: draft.proposalId } : {}),
          } : undefined,
        });
        if (outcome.ok) {
          discardDraft();
          close();
        }
      }}>
        {/* 1. 무엇을 했는가. 이 칸이 결과의 머리이고, 나머지는 그것에 붙는 것이다. */}
        <Field
          label="결과 요약"
          htmlFor={`return-summary-${handoff.handoffId}`}
          required={structured}
          hint="여기에 적으면 결과 요약과 고른 근거가 함께 기록됩니다. 비워 두면 아래 「메모만 남기기」의 한 줄만 기록됩니다."
          error={localError && !draft.summary.trim() ? localError : null}
        >
          <Textarea
            id={`return-summary-${handoff.handoffId}`}
            autoGrow
            value={draft.summary}
            invalid={Boolean(localError) && !draft.summary.trim()}
            onInput={(event) => set({ summary: event.currentTarget.value })}
          />
        </Field>

        {/* 2. 무엇을 들고 돌아가는가. 넘김이 들고 온 것뿐 아니라 이 Task의 모든 근거에서 고른다. */}
        <ReferencePicker
          idPrefix={`return-${handoff.handoffId}`}
          legend="결과로 남길 근거"
          hint="넘김이 들고 온 것뿐 아니라 이 Task의 모든 근거를 고를 수 있습니다."
          choices={choices}
          selectedIds={draft.referenceIds}
          onToggle={(referenceId) => set({ referenceIds: toggleSelectedIds(draft.referenceIds, referenceId) })}
          groupLabels={{ all: '전체', package: '넘김이 들고 온 것', task: '이 Task의 다른 근거' }}
          query={draft.resultQuery ?? ''}
          group={draft.resultGroup ?? 'all'}
          onFilter={({ query, group }) => set({ resultQuery: query, resultGroup: group })}
          emptyLabel="이 Task에 아직 붙은 근거가 없습니다. 아래에서 바로 연결할 수 있습니다."
        />
      </Form>

      {/* 3. 고르는 자리 **바로 옆**, 제출 **위**. 제출 아래에 두면 사람은 보내고 나서야 이 길을 본다. */}
      <div class="return-connect">
          <div class="return-connect__row">
            <p class="t-meta">결과로 남길 자료가 아직 Task에 없나요?</p>
            <Button size="sm" onClick={() => setConnecting(!connecting)}>
              {connecting ? '자료 연결 닫기' : '여기서 자료 연결'}
            </Button>
          </div>
        {connecting ? <InlineResourceConnect
          spaceId={spaceId}
          taskId={handoff.taskId ?? pack.task?.taskId}
          scope={workspace.scope}
          slot={`return:${handoff.handoffId}`}
          assets={assets}
          references={pack.references ?? []}
          done={() => setConnecting(false)}
        /> : null}
      </div>

      {/* 4. 선택 항목 — 접어 둔다. 다만 이미 적은 것이 있으면 펼친 채로 연다. */}
      <details class="return-extra" open={extraFilled}>
          {/* 자주 쓰지 않는 칸은 접어 두되, 무엇이 들어 있는지는 제목에 적는다.
              메모만 남기는 반환도 여전히 여기서 한다. */}
          <summary class="t-body-strong">추가 메모 및 제안 (선택)</summary>
          <div class="return-extra__body">
            <Field
              label="메모만 남기기"
              htmlFor={`return-note-${handoff.handoffId}`}
              hint="결과 요약을 비워 두고 이 한 줄만 적으면, 이 메모만 남기고 반환합니다."
            >
              <Input
                id={`return-note-${handoff.handoffId}`}
                value={draft.note}
                onInput={(event) => set({ note: event.currentTarget.value })}
              />
            </Field>
            <Field label="남은 문제 (한 줄씩)" htmlFor={`return-issues-${handoff.handoffId}`}>
              <Textarea
                id={`return-issues-${handoff.handoffId}`}
                autoGrow
                value={draft.remainingIssues}
                onInput={(event) => set({ remainingIssues: event.currentTarget.value })}
              />
            </Field>
            <Field
              label="관련 제안"
              htmlFor={`return-proposal-${handoff.handoffId}`}
              hint="이 일의 결론을 올려 둔 제안이 있으면 고릅니다. 골라도 승격되지는 않습니다."
            >
              <Select
                id={`return-proposal-${handoff.handoffId}`}
                value={draft.proposalId}
                onChange={(event) => set({ proposalId: event.currentTarget.value })}
              >
                <option value="">고르지 않음</option>
                {proposals.map((choice) => <option key={choice.proposalId} value={choice.proposalId}>
                  {proposalLabel(choice)}
                </option>)}
              </Select>
            </Field>
          </div>
        </details>

      {localError ? <Notice tone="danger" title={localError} /> : null}
      <CommandError cmd={returned}/>
      <Button kind="submit" form={formId} size="md" busy={returned.pending}>반환하기</Button>
    </div>
  </Dialog>;
}

export function ClosedHandoffRow({ handoff, name, assets, spaceId, pack }) {
  const finalEvent = handoff.events?.[handoff.events.length - 1];
  const result = readResult(handoff);
  const titles = assetTitleMap(assets);
  const view = readWorkPackage(handoff);
  // **결과가 가리키는 참조는 꾸러미 밖일 수 있다** — 일하면서 새로 붙인 근거가 보통 그렇다.
  // 꾸러미 항목만 찾으면 그런 참조는 이름 없는 id 로 남는다.
  const byId = new Map([
    ...(pack?.references ?? []).map((reference) => [reference.referenceId, reference]),
    ...(view?.items ?? []).map((item) => [item.referenceId, item]),
  ]);
  return <details class="promise-history__item">
    <summary class="t-body-strong">넘김 · {handoff.state === 'declined' ? '거절됨' : '반환됨'} · “{handoff.request}”</summary>
    <div class="stack">
      <p class="t-meta">{name(handoff.fromNodeId)} → {name(handoff.toNodeId)}</p>
      {finalEvent
        ? <p class="t-body">{name(finalEvent.actorNodeId)} · {handoffEvent(finalEvent.eventType)} · {provenance(finalEvent.actorProvenance)}{finalEvent.note ? ` · ${finalEvent.note}` : ''}</p>
        : <p class="t-meta">종결 기록을 읽지 못했습니다.</p>}
      {result ? <div class="stack">
        <p class="t-body">{result.summary}</p>
        {result.referenceIds.length ? <ul class="handoff-package__list" aria-label="반환이 남긴 근거">
          {result.referenceIds.map((id) => {
            const reference = byId.get(id) ?? { referenceId: id };
            return <li key={id} class="handoff-package__item">
              <p class="t-body">{referenceTitle(reference, titles)}</p>
              {reference.kind ? <ReferenceAction item={reference} spaceId={spaceId} assets={assets} /> : null}
            </li>;
          })}
        </ul> : null}
        {result.remainingIssues.length ? <>
          <p class="t-meta">남은 문제</p>
          <ul class="task-facts">{result.remainingIssues.map((issue) => <li key={issue}>{issue}</li>)}</ul>
        </> : null}
        {result.proposalId ? <p class="t-meta">관련 제안 {result.proposalId} — 이 포인터가 승격을 만들지는 않습니다.</p> : null}
      </div> : null}
    </div>
  </details>;
}

function SupersededProposalRow({ proposal, name }) {
  const declines = proposal.responses.filter((response) => response.response === 'decline');
  // 밀려난 제안도 서버가 **그 제안이 서 있던 옛 기준 위에서** 해석해 준 값으로만 읽는다.
  const view = readProposal(proposal);
  return <details class="promise-history__item"><summary class="t-body-strong">제안 p{proposal.number} · 다른 공동 이해로 대체됨{declines.length ? ` · 반대 ${declines.length}건` : ''}</summary><div class="stack">{view ? <><p class="t-meta">목적: {view.purpose} · 확정 {view.counts.confirmed} · 아직 모르는 것 {view.counts.openQuestions} · 근거 {view.counts.referenceIds}</p><Facts label="확정한 사실" items={view.confirmed} empty="없음"/><Facts label="아직 모르는 것" items={view.openQuestions} empty="없음"/></> : <p class="t-meta">이 제안이 만들 공동 이해를 아직 읽을 수 없습니다. 아래 응답 기록은 확인할 수 있습니다.</p>}{proposal.responses.length ? proposal.responses.map((response) => <p class="t-meta" key={response.responseId}>{name(response.actorNodeId)} · {response.response === 'accept' ? '수락' : '반대'} · {provenance(response.actorProvenance)}{response.rationale ? ` · ${response.rationale}` : ''}</p>) : <p class="t-meta">응답 기록이 없습니다.</p>}</div></details>;
}

function ProposalRow(props) { return <ProposalDecision {...props} />; }

function Composer({ kind, close, task, current, participants, spaceId, references, assets, workspace }) {
  const session = useSession();
  const cmd = taskCommand(kind === 'question' ? 'question_open' : 'proposal_submit', spaceId);
  const otherParticipants = participants.filter((participant) => participant.nodeId !== session.nodeId);
  const unavailable = ['question', 'handoff'].includes(kind) && otherParticipants.length === 0;
  const title = kind === 'question' ? '질문 열기' : kind === 'handoff' ? '넘김 제안' : '제안 올리기';
  if (unavailable) return <section class="task-form stack"><div class="row row--between"><p class="t-body-strong">{title}</p><Button size="sm" onClick={close}>닫기</Button></div><p class="t-meta">이 Task는 혼자 도는 Task입니다. 함께 볼 사람이 필요하면 대화에서 참여자를 고른 새 Task를 만드세요.</p></section>;
  // 넘김만 자기 작성기를 갖는다 — 받는 이·요청만 적던 자리가 아니라, **무엇을 들고 시작할지**
  // 고르는 자리가 됐기 때문이다. 나머지 둘은 아래 한 폼을 그대로 쓴다.
  if (kind === 'handoff') {
    return <HandoffComposer
      close={close}
      task={task}
      spaceId={spaceId}
      participants={otherParticipants}
      references={references ?? []}
      assets={assets}
      workspace={workspace}
    />;
  }
  return <Form class="task-form stack" busy={cmd.pending} onSubmit={(event) => { const form = new FormData(event.currentTarget); if (kind === 'question') cmd.run({ spaceId, taskId: task.taskId, prompt: form.get('prompt')?.trim(), recipientNodeIds: form.getAll('recipients') }); else cmd.run({ spaceId, taskId: task.taskId, baseVersionNo: task.currentVersionNo ?? null, candidateContext: { purpose: form.get('purpose')?.trim(), confirmed: lines(form.get('confirmed')), openQuestions: lines(form.get('openQuestions')), referenceIds: current?.referenceIds ?? [] } }); }}><div class="row row--between"><p class="t-body-strong">{title}</p><Button size="sm" onClick={close}>닫기</Button></div>{kind === 'question' ? <><Field label="질문" htmlFor="questionPrompt" required><Textarea id="questionPrompt" name="prompt" /></Field><Field label="지목할 참여자" htmlFor="questionRecipients" required><select id="questionRecipients" name="recipients" multiple class="ctl">{otherParticipants.map((participant) => <option value={participant.nodeId} key={participant.nodeId}>{participant.displayName ?? '이름 없는 자리'}</option>)}</select></Field></> : <><p class="t-meta">제안은 지금의 공동 이해를 덮어씁니다. 지우지 않은 것만 남습니다.</p><Field label="목적" htmlFor="purpose" required><Textarea id="purpose" name="purpose" defaultValue={current?.purpose ?? ''} /></Field><Field label="확정한 사실 (한 줄씩)" htmlFor="confirmed"><Textarea id="confirmed" name="confirmed" defaultValue={(current?.confirmed ?? []).map((item) => item.text).join('\n')} /></Field><Field label="아직 모르는 것 (한 줄씩)" htmlFor="openQuestions"><Textarea id="openQuestions" name="openQuestions" defaultValue={(current?.openQuestions ?? []).map((item) => item.text).join('\n')} /></Field></>}<CommandError cmd={cmd}/><Button kind="submit" size="md" busy={cmd.pending}>{kind === 'proposal' ? '제안 올리기' : '남기기'}</Button></Form>;
}

function References({ references, current, threads, spaceId, task, writable }) {
  const add = taskCommand('task_reference_add', spaceId);
  const session = useSession();
  const [adding, setAdding] = useState(null);
  const assets = useResource(`space:${spaceId}:resources`, () =>
    read('resources', { nodeId: session.nodeId, query: { spaceId } }),
  );
  const threadRows = threads.status === 'ready' ? threads.data.threads ?? [] : [];
  const topics = new Map(threadRows.map((thread) => [thread.threadId, thread.topic]));
  // 자료도 Task에 복사된 이름을 믿지 않는다. 같은 Space의 현재 manifest 목록을
  // 다시 읽어, locator가 가리키는 자료를 사람이 알아볼 이름으로만 보여 준다.
  const assetTitles = assets.status === 'ready'
    ? new Map((assets.data.assets ?? []).map((asset) => [asset.assetId, asset.title]))
    : new Map();
  const close = () => setAdding(null);
  return <Panel title="출처" action={writable ? <div class="task-actions"><Button size="sm" onClick={() => setAdding(adding === 'thread' ? null : 'thread')}>대화 연결</Button><Button size="sm" onClick={() => setAdding(adding === 'asset' ? null : 'asset')}>자료 연결</Button></div> : null}><div class="stack">
    {references.length ? <div class="promise-list">{references.map((reference) => { const threadId = reference.locator?.threadId; const readable = reference.providerAvailable && reference.capabilities?.read && reference.transport === 'native' && threadId; const asset = reference.locator?.assetId; const assetTitle = asset ? assetTitles.get(asset) : null; return <article class="promise" key={reference.referenceId}><div class="row"><Chip>{relation(reference.relation)}</Chip><Chip>{reference.mode === 'live' ? '계속 따라감' : '이 시점 고정'}</Chip>{current?.referenceIds.includes(reference.referenceId) ? <Chip tone="accepted">v{current.versionNo} 근거</Chip> : null}</div><p class="t-body">{asset ? assetTitle ?? '자료 위치를 다시 읽는 중' : topics.get(threadId) ?? '이름을 읽지 못한 대화'}</p>{readable ? <div class="row"><ButtonLink size="sm" href={`/s/${spaceId}/t/${threadId}`}>대화 열기</ButtonLink></div> : asset ? <div class="row"><p class="t-meta">자료 위치와 버전만 참조합니다. 본문을 Task에 복사하지 않습니다.</p><ButtonLink size="sm" href={resourceLink(spaceId, asset, pinnedAssetVersion(reference))}>자료 보기</ButtonLink></div> : <p class="t-meta">이 원문은 지금 이 화면에서 열 수 없습니다.</p>}</article>; })}</div> : <div class="reference-empty"><p class="t-body-strong">아직 연결된 근거가 없습니다.</p><p class="t-meta">대화나 자료의 위치만 연결합니다. 본문·파일·토큰은 Task에 복사하지 않습니다.</p>{writable ? <ButtonLink size="sm" href={`/s/${spaceId}/resources`}>자료 위치 연결</ButtonLink> : null}</div>}
    {adding === 'thread' ? <Form class="task-form stack" busy={add.pending} onSubmit={async (event) => { const form = new FormData(event.currentTarget); const threadId = form.get('threadId'); if (threadId) { const result = await add.run({ spaceId, taskId: task.taskId, kind: 'thread', relation: form.get('relation'), mode: 'live', locator: { threadId } }); if (result.ok) close(); } }}><div class="row row--between"><p class="t-body-strong">대화 근거 연결</p><Button size="sm" onClick={close}>닫기</Button></div>{threadRows.length ? <><Field label="대화" htmlFor={`reference-thread-${task.taskId}`} required><Select id={`reference-thread-${task.taskId}`} name="threadId"><option value="">고르기</option>{threadRows.map((thread) => <option key={thread.threadId} value={thread.threadId}>{thread.topic}</option>)}</Select></Field><Field label="관계" htmlFor={`reference-relation-${task.taskId}`} required><Select id={`reference-relation-${task.taskId}`} name="relation"><option value="evidence">근거</option><option value="working">작업 중</option><option value="decision">판단</option><option value="origin">시작점</option><option value="handoff">넘김</option></Select></Field><CommandError cmd={add}/><Button kind="submit" tone="human" size="md" busy={add.pending}>대화 근거 연결</Button></> : <p class="t-meta">근거로 연결할 대화가 없습니다.</p>}</Form> : null}
    {adding === 'asset' ? <AssetReferenceForm assets={assets} add={add} spaceId={spaceId} taskId={task.taskId} close={close} /> : null}
  </div></Panel>;
}

function AssetReferenceForm({ assets, add, spaceId, taskId, close }) {
  if (assets.status === 'loading') return <p class="t-meta">연결할 자료를 읽는 중입니다.</p>;
  if (assets.status === 'error') return <p class="t-meta">자료 목록을 읽지 못해 지금은 참조로 연결할 수 없습니다.</p>;
  const list = assets.data.assets ?? [];
  if (!list.length) return <p class="t-meta">먼저 자료 화면에서 원문 위치를 연결하면 여기서 Task 근거로 붙일 수 있습니다.</p>;
  return <Form class="task-form stack" busy={add.pending} onSubmit={async (event) => { const form = new FormData(event.currentTarget); const assetId = form.get('assetId'); const asset = list.find((item) => item.assetId === assetId); const mode = form.get('mode'); if (!asset) return; const locator = mode === 'snapshot' ? { assetId, version: asset.currentVersion ?? asset.registeredVersion } : { assetId }; const result = await add.run({ spaceId, taskId, kind: 'asset', relation: form.get('assetRelation'), mode, locator }); if (result.ok) close?.(); }}><div class="row row--between"><p class="t-body-strong">자료 근거 연결</p>{close ? <Button size="sm" onClick={close}>닫기</Button> : null}</div><Field label="자료" htmlFor={`reference-asset-${taskId}`} required><Select id={`reference-asset-${taskId}`} name="assetId"><option value="">고르기</option>{list.map((asset) => <option key={asset.assetId} value={asset.assetId}>{asset.title}</option>)}</Select></Field><div class="task-form__twocol"><Field label="관계" htmlFor={`reference-asset-relation-${taskId}`} required><Select id={`reference-asset-relation-${taskId}`} name="assetRelation"><option value="evidence">근거</option><option value="working">작업 중</option><option value="decision">판단</option></Select></Field><Field label="읽는 방식" htmlFor={`reference-asset-mode-${taskId}`} required><Select id={`reference-asset-mode-${taskId}`} name="mode"><option value="live">계속 따라감</option><option value="snapshot">이 시점 고정</option></Select></Field></div><p class="t-meta">자료 참조에는 providerId를 보내지 않습니다. asset locator만 Task에 남습니다.</p><CommandError cmd={add}/><Button kind="submit" tone="human" size="md" busy={add.pending}>자료 근거 연결</Button></Form>;
}

/** 참여자 아바타 — 시안 1e. AI 는 색이 아니라 점선 형태다. */
function ParticipantAvatar({ label, ai }) {
  const initial = typeof label === 'string' && label.length > 0 ? label.slice(0, 1) : '?';
  return <span class={`avatar${ai ? ' avatar--ai' : ''}`} aria-hidden="true">{initial}</span>;
}

/**
 * 넘김 작성기 — 받는 이와 요청뿐이던 자리에 **무엇을 들고 시작할지**가 붙었다.
 *
 * 세 가지를 한 화면에서 한다:
 *   1. 이 Task 에 이미 붙어 있는 근거 중 고르기 (필요도 · 읽는 순서 · 지시)
 *   2. 아직 이 Task 에 없는 자료를 **여기서** 붙이기 — 자료 화면으로 보내지 않는다
 *   3. 돌려받고 싶은 것 적기
 *
 * **보내는 것은 참조 id 뿐이다.** 제목도 주소도 본문도 이 요청에 실리지 않는다 —
 * Ledger 가 자산 사본을 갖기 시작하는 순간 그 경계가 무너지기 때문이다.
 * 기준 공동 이해는 여기서 고르지 않는다: 서버가 지금 값을 읽어 굳힌다.
 */
function HandoffComposer({ close, task, spaceId, participants, references, assets, workspace }) {
  const offer = taskCommand('handoff_offer', spaceId);
  // **초안은 컴포넌트 밖에 산다.** 안에 두면 자료를 연결하는 순간(= Task 맥락 무효화)
  // 이 컴포넌트가 unmount 되면서 적던 글이 사라진다 — 실제로 그렇게 났다.
  const [draft, setDraft, discardDraft] = useDraft(workspace.scope, 'handoff', {
    toNodeId: '',
    request: '',
    expectedBack: '',
    authority: '',
    expectedResult: '',
    picked: [],
    // 좁혀 찾던 말도 초안이다. 자료를 연결하면 이 작성기는 unmount 되는데,
    // 그때 검색어가 사라지면 사람은 방금 찾던 자리를 다시 찾아야 한다.
    pickQuery: '',
    // 작성기를 처음 연 시점에 보고 있던 이해. 여기서 옛 기준을 고르는 것이 아니라,
    // 내가 본 것을 확인받는 값이다.
    observedVersionNo: task.currentVersionNo ?? null,
  });
  const [stale, setStale] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const titles = assetTitleMap(assets);

  const picked = draft.picked;
  const toggle = (referenceId) => setDraft((prior) => ({
    ...prior,
    picked: prior.picked.some((item) => item.referenceId === referenceId)
      ? prior.picked.filter((item) => item.referenceId !== referenceId)
      : [...prior.picked, { referenceId, necessity: 'required', guidance: '' }],
  }));
  const updatePick = (referenceId, patch) => setDraft((prior) => ({
    ...prior,
    picked: prior.picked.map((item) => item.referenceId === referenceId ? { ...item, ...patch } : item),
  }));
  // 읽는 순서를 **고른 것을 유지한 채** 바꾼다. 예전에는 풀었다 다시 골라야 했고,
  // 푸는 순간 그 항목의 필요도·지시가 함께 사라졌다.
  const reorder = (referenceId, delta) => setDraft((prior) => {
    const order = moveSelectedId(prior.picked.map((item) => item.referenceId), referenceId, delta);
    return {
      ...prior,
      picked: order.map((id) => prior.picked.find((item) => item.referenceId === id)).filter(Boolean),
    };
  });
  const choices = (references ?? []).map((reference) => referenceChoice(reference, titles, assets, 'task'));

  return <section class="task-form stack">
    <div class="row row--between"><p class="t-body-strong">넘김 제안</p><Button size="sm" onClick={close}>닫기</Button></div>
    <Form class="stack" busy={offer.pending} onSubmit={async () => {
      const expectedResult = draft.expectedResult.trim();
      // 고른 것도 없고 기대 결과도 없으면 꾸러미를 만들지 않는다 — 옛 넘김과 같은 요청이다.
      const workPackage = picked.length || expectedResult
        ? {
          // 확인용이다. 지금 값과 다르면 서버가 409 로 끊고, 아래에서 그 사실을 말한다.
          baseVersionNo: draft.observedVersionNo,
          ...(expectedResult ? { expectedResult } : {}),
          references: picked.map((item) => ({
            referenceId: item.referenceId,
            necessity: item.necessity,
            ...(item.guidance.trim() ? { guidance: item.guidance.trim() } : {}),
          })),
        }
        : undefined;
      const outcome = await offer.run({
        spaceId,
        taskId: task.taskId,
        toNodeId: draft.toNodeId,
        request: draft.request.trim(),
        expectedBack: draft.expectedBack || undefined,
        authority: draft.authority.trim() || undefined,
        workPackage,
      });
      if (outcome.ok) {
        discardDraft();
        close();
        return;
      }
      // **조용히 다시 보내지 않는다.** 기준이 달라졌다는 것은 그 사이 누군가 합의를 옮겼다는
      // 뜻이고, 그 위에서 이 넘김이 여전히 맞는 말인지는 사람이 판단할 일이다.
      if (outcome.error?.code === 'handoff_base_stale') {
        setStale(outcome.error.details?.currentVersionNo ?? null);
      }
    }}>
      <Field label="받는 이" htmlFor="handoffTo" required>
        <Select id="handoffTo" value={draft.toNodeId} onChange={(event) => setDraft({ toNodeId: event.currentTarget.value })}>
          <option value="">고르기</option>
          {participants.map((participant) => <option key={participant.nodeId} value={participant.nodeId}>
            {participant.displayName ?? '이름 없는 자리'}
          </option>)}
        </Select>
      </Field>
      <Field label="요청" htmlFor="handoffRequest" required>
        <Textarea id="handoffRequest" autoGrow value={draft.request} onInput={(event) => setDraft({ request: event.currentTarget.value })} />
      </Field>
      <Field label="돌려받기 희망" htmlFor="expectedBack">
        <Input id="expectedBack" type="date" value={draft.expectedBack} onInput={(event) => setDraft({ expectedBack: event.currentTarget.value })} />
      </Field>
      <Field label="범위" htmlFor="authority">
        <Input id="authority" value={draft.authority} onInput={(event) => setDraft({ authority: event.currentTarget.value })} />
      </Field>
      <Field
        label="돌려받고 싶은 것"
        htmlFor="expectedResult"
        hint="무엇을 들고 돌아오면 되는지 한 줄로 적습니다."
      >
        <Textarea id="expectedResult" rows={2} autoGrow value={draft.expectedResult} onInput={(event) => setDraft({ expectedResult: event.currentTarget.value })} />
      </Field>

      {stale !== null ? <StaleBaseNotice
        spaceId={spaceId}
        taskId={task.taskId}
        observedVersionNo={draft.observedVersionNo}
        currentVersionNo={stale}
        onAdopt={() => { setDraft({ observedVersionNo: stale }); setStale(null); }}
        onDismiss={() => setStale(null)}
      /> : null}

      <ReferencePicker
        idPrefix="handoff"
        legend="들고 갈 근거 고르기"
        hint={`고른 순서가 읽는 순서가 됩니다. 기준은 이 작성기를 연 시점에 보고 계신${draft.observedVersionNo === null ? ' 이해 없음(v0)' : ` 공동 이해 v${draft.observedVersionNo}`}으로 굳습니다.`}
        choices={choices}
        selectedIds={picked.map((item) => item.referenceId)}
        onToggle={toggle}
        onMove={reorder}
        query={draft.pickQuery ?? ''}
        group="all"
        onFilter={({ query }) => setDraft({ pickQuery: query })}
        emptyLabel="이 Task에 아직 붙은 근거가 없습니다. 아래에서 바로 연결할 수 있습니다."
        renderSelected={(referenceId) => {
          const chosen = picked.find((item) => item.referenceId === referenceId);
          if (!chosen) return null;
          return <div class="stack stack--tight">
            <Field label="필요도" htmlFor={`need-${referenceId}`}>
              <Select
                id={`need-${referenceId}`}
                size="sm"
                value={chosen.necessity}
                onChange={(event) => updatePick(referenceId, { necessity: event.currentTarget.value })}
              >
                <option value="required">없으면 시작할 수 없음</option>
                <option value="optional">있으면 도움이 됨</option>
              </Select>
            </Field>
            <Field label="왜 이걸 보라는지" htmlFor={`guide-${referenceId}`}>
              <Input
                id={`guide-${referenceId}`}
                size="sm"
                value={chosen.guidance}
                onInput={(event) => updatePick(referenceId, { guidance: event.currentTarget.value })}
              />
            </Field>
          </div>;
        }}
      />

      <CommandError cmd={offer}/>
      <Button kind="submit" size="md" busy={offer.pending}>넘김 제안</Button>
    </Form>

    <div class="row row--between">
      <p class="t-meta">찾는 자료가 목록에 없나요?</p>
      <Button size="sm" onClick={() => setConnecting(!connecting)}>
        {connecting ? '자료 연결 닫기' : '여기서 자료 연결'}
      </Button>
    </div>
    {connecting ? <InlineResourceConnect
      spaceId={spaceId}
      taskId={task.taskId}
      scope={workspace.scope}
      slot="handoff"
      assets={assets}
      references={references}
      done={() => setConnecting(false)}
    /> : null}
  </section>;
}

/**
 * 기준이 그사이 바뀌었다 — **읽고, 사람이 고른다.**
 *
 * 처음 판은 자기 URL 로 가는 링크 하나였다. 그걸 누르면 적던 글이 사라지고, 새 이해가 무엇인지
 * 보지도 못한 채 다시 쓰게 된다. 여기서는 새 이해를 **그 자리에서 읽고**, 바꿀지 말지는
 * 명시적으로 누른다 — 자동 재기준은 하지 않는다(조용히 앞선 합의를 지우기 때문).
 */
function StaleBaseNotice({ spaceId, taskId, observedVersionNo, currentVersionNo, onAdopt, onDismiss }) {
  const session = useSession();
  const [reading, setReading] = useState(false);
  const version = useResource(
    reading && currentVersionNo !== null
      ? `space:${spaceId}:task:${taskId}:version:${currentVersionNo}`
      : null,
    () => read('task-version', {
      nodeId: session.nodeId,
      query: { spaceId, taskId, versionNo: currentVersionNo },
    }),
  );

  return <Notice tone="danger" title="이 Task의 공동 이해가 그사이 바뀌었습니다.">
    <p class="t-meta">
      {observedVersionNo === null ? '기준 없음(v0)' : `v${observedVersionNo}`}을 보고 쓰셨는데
      지금은 {currentVersionNo === null ? '다른 이해' : `v${currentVersionNo}`}입니다.
      적으신 내용은 그대로 있습니다.
    </p>
    {reading ? <Resource resource={version} loadingLabel="새 공동 이해를 읽는 중" compact>
      {(data) => <div class="stack">
        <p class="t-body">{data.purpose}</p>
        <Facts label="확정한 사실" items={data.confirmed ?? []} empty="없습니다." />
        <Facts label="아직 모르는 것" items={data.openQuestions ?? []} empty="없습니다." />
      </div>}
    </Resource> : null}
    <div class="row">
      {reading ? null : <Button size="sm" onClick={() => setReading(true)}>새 공동 이해 읽기</Button>}
      <Button size="sm" tone="human" onClick={onAdopt}>이 기준으로 바꿔 보내기</Button>
      <Button size="sm" tone="ghost" onClick={onDismiss}>그대로 두기</Button>
    </div>
  </Notice>;
}

/**
 * 작성기 안에서 바로 자료를 붙이는 자리.
 *
 * 두 길: 이미 이 Space 에 있는 자료를 Task 참조로 붙이거나, 없는 자료를 먼저 등록하고 붙인다.
 * **파일을 올리는 길은 없다** — Resource Cloud 는 manifest 정본이지 파일 저장소가 아니다.
 *
 * ## 등록은 됐는데 붙이기가 실패했을 때
 *
 * 처음 판은 제출할 때마다 `register.run` 을 다시 불렀다. 등록이 이미 성공한 뒤 붙이기만
 * 실패한 경우, 다시 누르면 **판이 하나 더 쌓인다** — 재시도가 아니라 새 사실을 만드는 쓰기다.
 * 그래서 성공한 등록은 붙들어 두고, 그 다음부터는 **붙이기만** 다시 한다. 등록 내용을 고치려면
 * 그건 다른 자료이므로 명시적으로 버리고 새로 등록한다.
 */
function InlineResourceConnect({ spaceId, taskId, scope, slot, assets, references, done }) {
  const session = useSession();
  const add = taskCommand('task_reference_add', spaceId);
  const register = useCommand(
    (params, idempotencyKey) => registerResource(params, { nodeId: session.nodeId, idempotencyKey }),
    { invalidates: [`space:${spaceId}:resources`] },
  );
  // 이 초안도 컴포넌트 밖에 산다: 붙이기가 성공하면 Task 맥락이 무효화되고 여기도 unmount 된다.
  const [draft, setDraft, discardDraft] = useDraft(scope, `connect:${slot}`, {
    mode: 'existing',
    sourceType: 'uri',
    assetId: '',
    kind: 'text',
    title: '',
    sourceUri: '',
    revision: '',
    commit: '',
    path: '',
    startLine: '',
    endLine: '',
    // 등록에 성공한 manifest. 있으면 다음 제출은 **붙이기만** 한다.
    staged: null,
    // 파일 올리기 쪽 초안. `file` 은 브라우저의 핸들이고(바이트 사본이 아니다),
    // 이 저장소는 이 탭의 메모리뿐이며 Task·Node 가 바뀌면 통째로 사라진다.
    file: null,
    upload: null,
  });
  const [localError, setLocalError] = useState(null);
  const capability = useUploadLimits(spaceId);

  const known = new Set((references ?? []).map((reference) => reference.locator?.assetId).filter(Boolean));
  const list = (assets?.status === 'ready' ? assets.data.assets ?? [] : []).filter((asset) => !known.has(asset.assetId));

  async function attach(assetId, version) {
    const outcome = await add.run({
      spaceId,
      taskId,
      kind: 'asset',
      relation: 'evidence',
      // 꾸러미에 담길 것이므로 **판을 고정한다.** live 로 붙이면 받는 쪽이 보는 것이
      // 보낸 쪽이 본 것과 다를 수 있고, 꾸러미는 그 사실을 숨길 수 없어야 한다.
      mode: 'snapshot',
      locator: { assetId, version },
    });
    return outcome.ok;
  }

  return <div class="task-form stack">
    <Field label="연결 방식" htmlFor={`inline-resource-mode-${slot}`}>
      <Select
        id={`inline-resource-mode-${slot}`}
        value={draft.mode}
        disabled={draft.staged !== null}
        onChange={(event) => setDraft({ mode: event.currentTarget.value })}
      >
        <option value="existing">이 Space에 이미 있는 자료</option>
        <option value="file">파일 올리기</option>
        <option value="new">새 원문 위치 등록</option>
      </Select>
    </Field>
    {draft.mode === 'file' && capability.state !== 'ready'
      ? <UploadUnavailableNotice capability={capability} />
      : null}

    {draft.staged !== null ? <div class="stack">
      {/* 등록은 끝났다. 여기서 다시 등록하면 판이 하나 더 쌓인다 — 그건 재시도가 아니다. */}
      <Notice tone="neutral" title="등록은 끝났습니다. 남은 것은 이 Task에 붙이는 일뿐입니다.">
        <p class="t-meta">{draft.staged.title} · 등록 판 v{draft.staged.registeredVersion}</p>
      </Notice>
      <CommandError cmd={add}/>
      <div class="row">
        <Button size="md" busy={add.pending} onClick={async () => {
          if (await attach(draft.staged.assetId, draft.staged.registeredVersion)) {
            discardDraft();
            done();
          }
        }}>붙이기만 다시</Button>
        <Button size="sm" tone="ghost" onClick={() => setDraft({ staged: null })}>
          다른 자료로 새로 등록
        </Button>
      </div>
    </div> : draft.mode === 'file' && capability.state === 'ready' ? (
      /* 자료실과 **같은 조각**을 쓴다. 폼을 만들지 않으므로 넘김·반환 폼 안에 그대로 들어간다.
         확정 뒤 붙이기가 실패하면 그 조각이 「붙이기만 다시」를 맡는다 — 다시 올리지 않는다. */
      <ResourceUploader
        spaceId={spaceId}
        limits={capability.limits}
        draft={draft}
        setDraft={setDraft}
        idPrefix={`inline-upload-${slot}`}
        attach={(manifest) => attach(manifest.assetId, manifest.registeredVersion)}
        onUploaded={() => {
          // 붙이기까지 끝난 **뒤에** 초안을 비운다. 중간에 비우면 붙이기가 실패했을 때
          // 「붙이기만 다시 시도」가 가리킬 판이 사라진다.
          discardDraft();
          done();
        }}
      />
    ) : draft.mode === 'existing' ? (
      list.length ? <Form class="stack" busy={add.pending} onSubmit={async () => {
        const asset = list.find((item) => item.assetId === draft.assetId);
        if (!asset) {
          setLocalError('붙일 자료를 고르세요.');
          return;
        }
        setLocalError(null);
        if (await attach(asset.assetId, asset.currentVersion ?? asset.registeredVersion)) {
          discardDraft();
          done();
        }
      }}>
        <Field label="자료" htmlFor={`inline-resource-asset-${slot}`} required error={localError}>
          <Select id={`inline-resource-asset-${slot}`} value={draft.assetId} onChange={(event) => setDraft({ assetId: event.currentTarget.value })}>
            <option value="">고르기</option>
            {list.map((asset) => <option key={asset.assetId} value={asset.assetId}>{asset.title}</option>)}
          </Select>
        </Field>
        <CommandError cmd={add}/>
        <Button kind="submit" size="md" busy={add.pending}>이 Task의 근거로 붙이기</Button>
      </Form> : <p class="t-meta">이 Space에 아직 붙일 수 있는 자료가 없습니다. 아래에서 새로 등록하세요.</p>
    ) : (
      <Form class="stack" busy={register.pending || add.pending} onSubmit={async () => {
        const title = draft.title.trim();
        const sourceUri = draft.sourceUri.trim();
        if (!title || !sourceUri) {
          setLocalError('자료 이름과 원문 위치를 모두 적으세요.');
          return;
        }
        const source = draft.sourceType === 'git' ? gitDescriptorFrom(draft) : { type: 'uri' };
        if (source === null) {
          setLocalError('git 자료에는 전체 commit 해시와 저장소 기준 상대 경로가 필요합니다.');
          return;
        }
        setLocalError(null);
        const registered = await register.run({
          spaceId,
          kind: draft.kind,
          title,
          sourceUri,
          revision: draft.revision.trim() || undefined,
          source,
        });
        if (!registered.ok) return;
        // **성공한 등록을 붙들어 둔다.** 이 다음 붙이기가 실패해도 다시 등록하지 않는다.
        const manifest = registered.data;
        setDraft({ staged: manifest });
        if (await attach(manifest.assetId, manifest.registeredVersion)) {
          discardDraft();
          done();
        }
      }}>
        <Field label="주소 방식" htmlFor={`inline-source-type-${slot}`} hint="git 자료는 주소 하나로 다시 찾을 수 없습니다 — 판·파일·줄이 함께 필요합니다.">
          <Select id={`inline-source-type-${slot}`} value={draft.sourceType} onChange={(event) => setDraft({ sourceType: event.currentTarget.value })}>
            <option value="uri">URL 하나</option>
            <option value="git">git 저장소의 파일</option>
          </Select>
        </Field>
        <Field label="종류" htmlFor={`inline-resource-kind-${slot}`}>
          <Select id={`inline-resource-kind-${slot}`} value={draft.kind} onChange={(event) => setDraft({ kind: event.currentTarget.value })}>
            <option value="text">문서</option>
            <option value="url">URL</option>
            <option value="image">이미지</option>
            <option value="video">동영상</option>
          </Select>
        </Field>
        <Field label="자료 이름" htmlFor={`inline-resource-title-${slot}`} required>
          <Input id={`inline-resource-title-${slot}`} value={draft.title} onInput={(event) => setDraft({ title: event.currentTarget.value })} />
        </Field>
        <Field
          label={draft.sourceType === 'git' ? '저장소 주소' : '원문 위치'}
          htmlFor={`inline-resource-uri-${slot}`}
          required
          hint="본문·파일·토큰을 붙이지 말고, 원문으로 돌아갈 주소만 적습니다."
        >
          <Input id={`inline-resource-uri-${slot}`} type="url" inputMode="url" value={draft.sourceUri} onInput={(event) => setDraft({ sourceUri: event.currentTarget.value })} />
        </Field>
        {draft.sourceType === 'git' ? <>
          <Field label="commit" htmlFor={`inline-git-commit-${slot}`} required hint="브랜치·태그는 판이 아닙니다. 전체 해시를 적습니다.">
            <Input id={`inline-git-commit-${slot}`} value={draft.commit} onInput={(event) => setDraft({ commit: event.currentTarget.value })} />
          </Field>
          <Field label="저장소 기준 경로" htmlFor={`inline-git-path-${slot}`} required>
            <Input id={`inline-git-path-${slot}`} value={draft.path} onInput={(event) => setDraft({ path: event.currentTarget.value })} />
          </Field>
          <div class="task-form__twocol">
            <Field label="시작 줄 (선택)" htmlFor={`inline-git-start-${slot}`}>
              <Input id={`inline-git-start-${slot}`} inputMode="numeric" value={draft.startLine} onInput={(event) => setDraft({ startLine: event.currentTarget.value })} />
            </Field>
            <Field label="끝 줄 (선택)" htmlFor={`inline-git-end-${slot}`}>
              <Input id={`inline-git-end-${slot}`} inputMode="numeric" value={draft.endLine} onInput={(event) => setDraft({ endLine: event.currentTarget.value })} />
            </Field>
          </div>
        </> : null}
        <Field label="원문 버전 (선택)" htmlFor={`inline-resource-revision-${slot}`}>
          <Input id={`inline-resource-revision-${slot}`} value={draft.revision} onInput={(event) => setDraft({ revision: event.currentTarget.value })} />
        </Field>
        <p class="t-meta">파일을 올리는 길은 없습니다. 원문은 원본 시스템에 그대로 있고, 여기에는 위치만 남습니다.</p>
        {localError ? <Notice tone="danger" title={localError} /> : null}
        {register.error ? <Notice tone="danger" title={describeError(register.error).message} /> : null}
        <CommandError cmd={add}/>
        <Button kind="submit" size="md" busy={register.pending || add.pending}>등록하고 근거로 붙이기</Button>
      </Form>
    )}
  </div>;
}

/** 초안이 적은 git 서술자. 모양이 안 맞으면 null — 서버가 거절하기 전에 사람에게 먼저 말한다. */
function gitDescriptorFrom(draft) {
  const commit = draft.commit.trim();
  const path = draft.path.trim();
  if (!commit || !path) return null;
  const startLine = Number.parseInt(draft.startLine, 10);
  const endLine = Number.parseInt(draft.endLine, 10);
  const range = Number.isInteger(startLine) && Number.isInteger(endLine) ? { startLine, endLine } : null;
  return { type: 'git', commit, path, ...(range ? { range } : {}) };
}

/**
 * 참조 하나를 사람이 알아볼 한 줄로.
 *
 * Ledger 에는 제목이 없다(locator 뿐이다). 그래서 자료 이름은 **지금 다시 읽은** manifest
 * 목록에서 온다. 못 읽으면 지어내지 않고 무엇인지만 말한다.
 */
/**
 * 고르개 한 줄. 제목 아래에 **무엇의 어느 판인가**를 붙인다.
 *
 * 자료 참조면 그 자료가 무엇인지도 함께 적는다 — 보관 파일은 이름·크기가, git 자료는
 * 경로·commit 이 같은 제목의 둘을 구별하는 자리다. 없는 주소를 지어내지 않는다.
 */
function referenceChoice(reference, titles, assets, group) {
  const manifest = reference.locator?.assetId ? assetById(assets, reference.locator.assetId) : null;
  const identity = referenceIdentity({
    relationLabel: relation(reference.relation),
    mode: reference.mode,
    pinnedVersion: pinnedAssetVersion(reference),
  });
  return {
    referenceId: reference.referenceId,
    title: referenceTitle(reference, titles),
    meta: manifest ? `${identity} · ${assetSourceSummary(manifest)}` : identity,
    group,
  };
}

function referenceTitle(item, titles) {
  const assetId = item.locator?.assetId;
  if (assetId) return titles.get(assetId) ?? '이름을 읽지 못한 자료';
  if (item.locator?.url) return item.locator.url;
  if (item.locator?.threadId) return item.kind === 'message' ? '대화 속 한 발화' : '대화 기록';
  return item.referenceId;
}

function assetTitleMap(assets) {
  return assets?.status === 'ready'
    ? new Map((assets.data.assets ?? []).map((asset) => [asset.assetId, asset.title]))
    : new Map();
}

function assetById(assets, assetId) {
  if (!assetId || assets?.status !== 'ready') return null;
  return (assets.data.assets ?? []).find((asset) => asset.assetId === assetId) ?? null;
}

function taskCommand(operation, spaceId) { const session = useSession(); return useCommand((params, idempotencyKey) => command(operation, params, { nodeId: session.nodeId, idempotencyKey }), { invalidates: [`space:${spaceId}:task`, `space:${spaceId}:tasks`, `space:${spaceId}:inbox`, `space:${spaceId}:conversation-activity`] }); }
function CommandError({ cmd }) { return cmd.error ? <Notice tone="danger" title={describeError(cmd.error).message} /> : null; }
const countTotal = (counts) => (counts?.questions ?? 0) + (counts?.handoffs ?? 0) + (counts?.proposals ?? 0);
const lines = (value) => String(value ?? '').split('\n').map((text, index) => ({ id: `entry_${index + 1}`, text: text.trim() })).filter((item) => item.text);
const date = (value) => value ? new Intl.DateTimeFormat('ko-KR', { dateStyle: 'medium' }).format(new Date(value)) : '';
const roleLabel = (role) => ({ owner: '오너', participant: '참여자', observer: '관찰자', none: '없음' })[role] ?? role;
const provenance = (value) => ({ human_direct: '사람 직접', autonomous_ai: 'AI 자율', delegated_ai: '위임 AI' })[value] ?? value;
const relation = (value) => ({ origin: '시작점', working: '작업 중', decision: '판단', evidence: '근거', handoff: '넘김' })[value] ?? value;
const handoffEvent = (value) => ({ accepted: '수락', declined: '거절', returned: '반환' })[String(value ?? '').split('.').at(-1)] ?? value;
